import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AcuStorageService } from './acustorage.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface FeedbackPayload {
  rating: number;
  comment: string;
  suggestion?: string;
  context: string;
}

export interface FeedbackRecord {
  _id: string;
  rating: number;
  comment: string;
  suggestion?: string;
  context: string;
  createdAt: string;
}

export interface FeedbackStats {
  averageRating: number;
  totalFeedbacks: number;
  ratingDistribution: { [key: string]: number };
}

const FEEDBACK_LAST_SHOWN_KEY = 'feedback_last_shown';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {

  private endpointFeedback = '/feedback';

  // Global modal state for On-Demand feedback (e.g. from Side Menu or Profile)
  private feedbackModalSubject = new BehaviorSubject<{ isOpen: boolean; context: string }>({ isOpen: false, context: 'manual' });
  public feedbackModal$ = this.feedbackModalSubject.asObservable();

  constructor(
    private http: HttpClient,
    private acuStorage: AcuStorageService
  ) {}

  /**
   * Opens the feedback modal on demand from any place in the app.
   */
  openFeedbackModal(context: string = 'manual'): void {
    this.feedbackModalSubject.next({ isOpen: true, context });
  }

  /**
   * Closes the on-demand feedback modal.
   */
  closeFeedbackModal(): void {
    this.feedbackModalSubject.next({ isOpen: false, context: 'manual' });
  }

  /**
   * Returns true if the feedback popup should be shown automatically (e.g. after booking).
   * In production this returns false if enableFeedbackAutoPopup is false.
   */
  shouldShowFeedback(): boolean {
    if (!environment.enableFeedback) return false;
    const autoPopupEnabled = (environment as any).enableFeedbackAutoPopup !== undefined
      ? (environment as any).enableFeedbackAutoPopup
      : true;
    if (!autoPopupEnabled) return false;

    const lastShown = this.acuStorage.getObjectInStorage<number>(FEEDBACK_LAST_SHOWN_KEY);
    if (!lastShown) return true;
    return (Date.now() - lastShown) > ONE_WEEK_MS;
  }

  /**
   * Records that the feedback popup was shown (resets the weekly timer).
   */
  markFeedbackShown(): void {
    this.acuStorage.setObjectInStorage<number>(FEEDBACK_LAST_SHOWN_KEY, Date.now());
  }

  /**
   * Submits anonymous feedback to the backend.
   */
  async submitFeedback(payload: FeedbackPayload): Promise<{ success: boolean; id: string }> {
    return await this.http.post<{ success: boolean; id: string }>(
      environment.apiUrl + this.endpointFeedback,
      payload
    ).toPromise();
  }

  // ─── Admin/HR APIs ─────────────────────────────────────────────────────────

  async getAdminFeedbacks(params?: {
    page?: number;
    limit?: number;
    rating?: string;
    startDate?: string;
    endDate?: string;
    context?: string;
  }): Promise<{ feedbacks: FeedbackRecord[]; totalItems: number; totalPages: number }> {
    let queryString = '';
    if (params) {
      const parts: string[] = [];
      if (params.page) parts.push(`page=${params.page}`);
      if (params.limit) parts.push(`limit=${params.limit}`);
      if (params.rating) parts.push(`rating=${params.rating}`);
      if (params.startDate) parts.push(`startDate=${params.startDate}`);
      if (params.endDate) parts.push(`endDate=${params.endDate}`);
      if (params.context) parts.push(`context=${params.context}`);
      if (parts.length) queryString = '?' + parts.join('&');
    }
    return await this.http.get<{ feedbacks: FeedbackRecord[]; totalItems: number; totalPages: number }>(
      environment.apiUrl + this.endpointFeedback + queryString
    ).toPromise();
  }

  async getAdminFeedbackStats(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<FeedbackStats> {
    let queryString = '';
    if (params) {
      const parts: string[] = [];
      if (params.startDate) parts.push(`startDate=${params.startDate}`);
      if (params.endDate) parts.push(`endDate=${params.endDate}`);
      if (parts.length) queryString = '?' + parts.join('&');
    }
    return await this.http.get<FeedbackStats>(
      environment.apiUrl + this.endpointFeedback + '/stats' + queryString
    ).toPromise();
  }
}
